import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Save,
  Eye,
  Pencil,
  History,
  ChevronRight,
  Loader2,
  Trash2,
  AlertTriangle,
  FileWarning,
  ExternalLink,
  Upload,
  RotateCw,
  FileCog,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { VaultTree } from '@/features/vault/vault-tree'
import { VaultEditor } from '@/features/vault/vault-editor'
import { VaultPreview } from '@/features/vault/vault-preview'
import { parseFrontmatter, fmString } from '@/features/vault/frontmatter'
import {
  useVaultFiles,
  useVaultFile,
  usePutVaultFile,
  useDeleteVaultFile,
  useBulkUploadVaultFiles,
  useIngestionJobs,
  useRetryIngestionJob,
  useReparseVaultFile,
} from '@/features/vault/use-vault'
import { useRemoveDemoContent } from '@/features/demo/use-demo'
import type { IngestionJob, VaultBulkConflictStrategy, VaultBulkUploadResultItem } from '@/features/vault/vault-api'

// Dateien starten bewusst in der Vorschau. Bearbeitung bleibt ein expliziter
// Klick, damit HTML-Outputs und Markdown-Dossiers nicht als Quelltext ueberfallen.
function isReadOnlyByDefault(path: string): boolean {
  return Boolean(path)
}

function isResearchSource(path: string): boolean {
  return path.startsWith('_Research/Sources/')
}

// Praefixe, die das Wissens-Retrieval (search_vault) abdeckt — Spiegelung von
// VAULT_CONTEXT_PATH_PREFIXES in apps/backend/src/services/chat-context.ts.
// Dateien darunter sind per FTS durchsuchbar, sobald sie gespeichert sind.
const KNOWLEDGE_COVERED_PREFIXES = [
  '_Knowledge/',
  '_Research/Dossiers/',
  '_Research/Sources/',
  '_Inbox/',
  'Notes/',
  '_Data/',
  '_Beispiele/',
]

// Simple debounce hook
function useDebounce<T extends unknown[]>(fn: (...args: T) => void, delay: number) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  return useCallback(
    (...args: T) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => fn(...args), delay)
    },

    [fn, delay],
  )
}

type ViewMode = 'edit' | 'preview'

// Toolbar breadcrumb
function Breadcrumb({ path }: { path: string }) {
  const parts = path.split('/')
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-0.5 font-mono text-xs text-muted-foreground min-w-0 flex-1">
      {parts.map((part, i) => (
        <span key={i} className="flex items-center gap-0.5 min-w-0">
          {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/40" />}
          <span className={cn('truncate', i === parts.length - 1 && 'text-foreground font-medium')}>
            {part}
          </span>
        </span>
      ))}
    </nav>
  )
}

// F05a2: New-File-Dialog auf Radix-Dialog-Primitive umgestellt. Genau eine
// Instanz in VaultPage (kein DialogTrigger pro Launcher — mehrere Roots mit
// gemeinsamem open-State würden mehrere Inhalte/Fokusfallen gleichzeitig
// öffnen). Der tatsächlich geklickte Auslöser wird in VaultPage erfasst und
// hier per onCloseAutoFocus wieder fokussiert. Formular, Texte und Handler
// bleiben erhalten.
interface NewFileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (path: string) => void
  onCloseAutoFocus: (event: Event) => void
}
function NewFileDialog({ open, onOpenChange, onConfirm, onCloseAutoFocus }: NewFileDialogProps) {
  const [value, setValue] = useState('')
  const handleOpenChange = (next: boolean) => {
    if (!next) setValue('')
    onOpenChange(next)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = value.trim()
    if (!trimmed) return
    const path = trimmed.endsWith('.md') ? trimmed : `${trimmed}.md`
    setValue('')
    onOpenChange(false)
    onConfirm(path)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Neue Datei</DialogTitle>
            <DialogDescription>Neuer Dateiname im Vault, relativ zum Workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="new-file-name" className="text-xs text-muted-foreground">Neuer Dateiname</Label>
            <Input
              id="new-file-name"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Notes/neue-notiz.md"
              className="h-9 text-sm font-mono"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" size="sm" disabled={!value.trim()}>
              Erstellen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// F05a2: Delete-Dialog auf Radix-Dialog-Primitive umgestellt. Genau eine
// Instanz in VaultPage; der geklickte Löschen-Button wird in VaultPage erfasst
// und per onCloseAutoFocus wieder fokussiert. Während des Löschens bleibt der
// Dialog offen (bestehende Pending-Sperre: Escape/Outside/X blockiert) und
// Texte/Handler bleiben erhalten.
interface DeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  path: string
  onConfirm: () => void
  isLoading: boolean
  onCloseAutoFocus: (event: Event) => void
}
function DeleteDialog({ open, onOpenChange, path, onConfirm, isLoading, onCloseAutoFocus }: DeleteDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Laufendes Löschen darf nicht scheinbar abgebrochen werden.
        if (isLoading && !next) return
        onOpenChange(next)
      }}
    >
      <DialogContent
        className="max-w-sm"
        closeDisabled={isLoading}
        onEscapeKeyDown={(event) => {
          if (isLoading) event.preventDefault()
        }}
        onPointerDownOutside={(event) => {
          if (isLoading) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (isLoading) event.preventDefault()
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
          <div>
            <DialogHeader className="pr-0">
              <DialogTitle>Datei löschen?</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mt-1 font-mono break-all">{path}</p>
            <DialogDescription className="mt-1 text-xs">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Löschen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function IngestionStatusBadge({ job }: { job: IngestionJob }) {
  const result = job.result && typeof job.result === 'object'
    ? job.result as { summary?: unknown; tags?: unknown; entities?: unknown; title?: unknown }
    : null
  const summary = typeof result?.summary === 'string' ? result.summary : null
  const title = typeof result?.title === 'string' ? result.title : null
  const tags = Array.isArray(result?.tags) ? result.tags.filter((tag): tag is string => typeof tag === 'string') : []
  const errorText = job.last_error ?? job.error ?? null
  const label = job.status === 'completed'
    ? 'Verarbeitung abgeschlossen'
    : job.status === 'completed_with_warnings'
      ? 'Verarbeitung mit Hinweisen'
      : job.status === 'failed'
        ? 'Analyse fehlgeschlagen'
        : job.status === 'cancelled'
          ? 'Analyse abgebrochen'
          : job.status === 'processing'
            ? 'Analyse läuft'
            : 'Wird analysiert'

  return (
    <span
      className={cn(
        'max-w-[320px] truncate rounded-md border px-2 py-1 text-xs',
        job.status === 'completed' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
        job.status === 'completed_with_warnings' && 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
        job.status === 'failed' && 'border-destructive/30 bg-destructive/10 text-destructive',
        job.status === 'cancelled' && 'border-border bg-muted text-muted-foreground',
        job.status === 'processing' && 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300',
        job.status === 'pending' && 'border-border bg-muted text-muted-foreground',
      )}
      title={[
        title,
        summary,
        tags.length > 0 ? `Tags: ${tags.join(', ')}` : null,
        errorText ? `Letzter Fehler: ${errorText}` : null,
        job.attempts != null && job.max_attempts != null
          ? `Versuche: ${job.attempts}/${job.max_attempts}`
          : null,
      ].filter(Boolean).join('\n')}
    >
      {label}{title ? ` · ${title}` : ''}
    </span>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// F05a: ehrliche Bestandsanzeige statt Funnel. Die drei Angaben sind
// unabhängig und zaehlen verschiedene Dinge — es gibt keine Pfeile, die eine
// Mengenbeziehung zwischen den Schritten suggerieren.
// - Gespeichert: alle Quelldateien im Workspace (ohne _Outputs/).
// - Verarbeitet: Dateien im Wissensbereich mit abgeschlossenen Ingestion-Jobs
//   (completed/completed_with_warnings). "Verarbeitet" heisst nicht garantiert
//   KI-analysiert: runIngestion kann bei fehlgeschlagener LLM-Analyse ein
//   Fallback-Result liefern und trotzdem completed melden.
// - Im Wissensbereich: Dateien unter den vom Retrieval abgedeckten Pfaden.
//   Das bedeutet nicht fachlich freigegeben oder fuer KI-Antworten zugelassen.
function SourcePipeline({
  stored,
  processed,
  covered,
  currentSelection,
  action,
}: {
  stored: number
  processed: number
  covered: number
  currentSelection?: string | null
  action?: React.ReactNode
}) {
  const items = [
    { label: 'Gespeichert', count: stored },
    { label: 'Verarbeitet', count: processed },
    { label: 'Im Wissensbereich', count: covered },
  ]

  return (
    <div
      data-testid="source-pipeline"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted/30 px-4 py-2 text-xs sm:gap-x-4"
    >
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <span className="flex min-w-6 items-center justify-center rounded-full border border-border bg-background px-1.5 py-0.5 text-xs font-semibold text-foreground">
            {item.count}
          </span>
          <span className="font-medium text-foreground">{item.label}</span>
        </div>
      ))}
      <span className="text-xs text-muted-foreground">
        Diese Angaben bestätigen keine KI-Analyse oder fachliche Freigabe.
      </span>
      {currentSelection && (
        <span className="hidden min-w-0 font-mono text-xs text-muted-foreground md:inline-flex md:items-baseline md:gap-1" title={currentSelection}>
          <span className="shrink-0">Auswahl:</span>
          <span className="inline-block max-w-[240px] truncate align-bottom">{currentSelection}</span>
        </span>
      )}
      {action ? <div className="ml-auto shrink-0">{action}</div> : null}
    </div>
  )
}

function BulkUploadResultItem({ item }: { item: VaultBulkUploadResultItem }) {
  const statusLabel = item.status === 'created'
    ? 'hochgeladen'
    : item.status === 'overwritten'
      ? 'überschrieben'
      : item.status === 'skipped'
        ? 'übersprungen'
        : item.status === 'error'
          ? 'Uploadfehler'
          : item.status

  const ingestionLabel = item.ingestionStatus === 'completed'
    ? 'Verarbeitung abgeschlossen'
    : item.ingestionStatus === 'completed_with_warnings'
      ? 'Verarbeitung mit Hinweisen'
      : item.ingestionStatus === 'failed'
        ? 'Analyse fehlgeschlagen'
        : item.ingestionStatus === 'cancelled'
          ? 'Analyse abgebrochen'
          : item.ingestionStatus === 'processing'
            ? 'Analyse läuft'
            : item.ingestionStatus === 'pending'
              ? 'Analyse ausstehend'
              : null

  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/30 last:border-0">
      <div className="min-w-0 flex-1">
        <span className="truncate font-mono block text-[11px] leading-tight" title={item.path ?? item.filename}>
          {item.path ?? item.filename}
        </span>
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-muted-foreground">
          {item.parserError && (
            <span className="text-amber-600 dark:text-amber-400">
              <FileWarning className="inline h-3 w-3 mr-0.5" />
              Datei konnte nicht gelesen werden: {item.parserError}
            </span>
          )}
          {item.ingestionError && (
            <span className="text-amber-600 dark:text-amber-400">
              <FileWarning className="inline h-3 w-3 mr-0.5" />
              Analyse konnte nicht gestartet werden: {item.ingestionError}
            </span>
          )}
          {item.message && item.status === 'error' && (
            <span className="text-destructive">{item.message}</span>
          )}
          {ingestionLabel && (
            <span className={item.ingestionStatus === 'failed' ? 'text-destructive' : item.ingestionStatus === 'processing' ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}>
              {ingestionLabel}
            </span>
          )}
        </div>
      </div>
      <span className={cn(
        'shrink-0 text-[10px] font-medium',
        item.status === 'error' && 'text-destructive',
        item.parserError && item.status !== 'error' && 'text-amber-600',
        !item.parserError && item.status === 'created' && 'text-emerald-600',
        !item.parserError && item.status === 'overwritten' && 'text-emerald-600',
        item.status === 'skipped' && 'text-amber-600',
        !item.parserError && item.status !== 'error' && item.status !== 'created' && item.status !== 'overwritten' && item.status !== 'skipped' && 'text-muted-foreground',
      )}>
        {statusLabel}
      </span>
    </div>
  )
}

interface BulkUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCloseAutoFocus: (event: Event) => void
}
function BulkUploadDialog({
  open,
  onOpenChange,
  onCloseAutoFocus,
}: BulkUploadDialogProps) {
  const [files, setFiles] = useState<File[]>([])
  const [targetFolder, setTargetFolder] = useState('_Inbox')
  const [customFolder, setCustomFolder] = useState('')
  const [conflictStrategy, setConflictStrategy] = useState<VaultBulkConflictStrategy>('rename')
  const [results, setResults] = useState<VaultBulkUploadResultItem[] | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const upload = useBulkUploadVaultFiles()
  const { data: ingestionData } = useIngestionJobs()
  const resolvedTargetFolder = targetFolder === '__custom__' ? customFolder.trim() : targetFolder

  const totalSize = files.reduce((sum, f) => sum + f.size, 0)
  const liveResults = useMemo(() => {
    if (!results) return null
    const jobs = ingestionData?.jobs ?? []
    return results.map((item) => {
      const job = jobs.find((candidate) =>
        (item.ingestionJobId && candidate.id === item.ingestionJobId)
        || (!item.ingestionJobId && item.path && candidate.vault_file_path === item.path),
      )
      return job ? { ...item, ingestionStatus: job.status } : item
    })
  }, [ingestionData?.jobs, results])

  const submit = async () => {
    if (files.length === 0) return
    if (!resolvedTargetFolder) return
    setUploadError(null)
    setResults(null)
    try {
      const result = await upload.mutateAsync({ files, targetFolder: resolvedTargetFolder, conflictStrategy })
      setResults(result.results)
      const items = result.results
      const errors = items.filter((item) => item.status === 'error').length
      const skipped = items.filter((item) => item.status === 'skipped').length
      const parserErrors = items.filter((item) => item.parserError).length
      const ingestionErrors = items.filter((item) => item.ingestionError).length
      const written = items.length - errors - skipped
      const parts = [`${written} importiert`]
      if (skipped > 0) parts.push(`${skipped} übersprungen`)
      if (errors > 0) parts.push(`${errors} Fehler`)
      if (parserErrors > 0) parts.push(`${parserErrors} nicht lesbar`)
      if (ingestionErrors > 0) parts.push(`${ingestionErrors} ohne Analyse-Start`)
      toast.success(parts.join(', '))
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bulk-Upload fehlgeschlagen'
      setUploadError(msg)
      toast.error(msg)
    }
  }

  // F05a2: Radix-Dialog statt eigenem fixed overlay. Genau eine Instanz in
  // VaultPage; der tatsächlich geklickte Auslöser (Tree-Header oder
  // Seiten-Empty-State) wird in VaultPage erfasst und per onCloseAutoFocus
  // wieder fokussiert. Während eines laufenden Imports bleibt der Dialog offen
  // (bestehende Pending-Sperre): Escape, Close-X und Overlay-Click werden
  // blockiert und der Close-X ist sichtbar deaktiviert (closeDisabled).
  // Wie das frühere bedingte Unmounten: Import-State wird beim Schliessen
  // konsistent zurückgesetzt, damit ein erneutes Öffnen nicht alte Ergebnisse
  // oder Fehler zeigt. Der Entwurf (ausgewählte Dateien) bleibt erhalten,
  // solange der Dialog offen ist.
  const handleOpenChange = (next: boolean) => {
    if (upload.isPending && !next) return
    if (!next) {
      setFiles([])
      setTargetFolder('_Inbox')
      setCustomFolder('')
      setConflictStrategy('rename')
      setResults(null)
      setUploadError(null)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="bulk-upload-dialog"
        className="max-w-xl"
        closeLabel="Importdialog schliessen"
        closeDisabled={upload.isPending}
        onEscapeKeyDown={(event) => {
          if (upload.isPending) event.preventDefault()
        }}
        onPointerDownOutside={(event) => {
          if (upload.isPending) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (upload.isPending) event.preventDefault()
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <div className="shrink-0 space-y-1">
          <DialogHeader>
            <DialogTitle>Dateien importieren</DialogTitle>
            <DialogDescription>
              Mehrere .md/.markdown/.txt/.pdf/.docx/.xlsx/.csv/.pptx-Dateien gleichzeitig in den Vault übernehmen.
              Originaldateien auf dem Computer bleiben unverändert. Binärdateien werden als Arbeitskopie importiert.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Hinweis: PDF-, DOCX- und XLSX-Dateien werden als lesbare Arbeitskopie importiert.
            Die Originaldatei bleibt unverändert.
          </p>
        </div>

        {/* The whole dialog scrolls so its controls remain reachable even
            when the header and footer exceed a short viewport together. */}
        <div>
          {/* File selection area */}
          {!upload.isPending && !results && (
            <div className="space-y-3">
              <label
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    event.currentTarget.querySelector('input')?.click()
                  }
                }}
                className="flex shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border px-4 py-8 text-sm text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Upload className="h-4 w-4" />
                <span>{files.length > 0 ? `${files.length} Datei(en) ausgewählt (${formatBytes(totalSize)})` : 'Dateien hier auswählen'}</span>
                <input
                  data-testid="bulk-upload-input"
                  type="file"
                  multiple
                  accept=".md,.markdown,.txt,.pdf,.docx,.xlsx,.csv,.tsv,.pptx"
                  className="hidden"
                  onChange={(event) => {
                    const selected = Array.from(event.target.files ?? [])
                    setFiles(selected)
                    setResults(null)
                    setUploadError(null)
                  }}
                />
              </label>

              {/* File name preview */}
              {files.length > 0 && (
                <div className="max-h-32 overflow-auto rounded-md border border-border bg-muted/20 p-2 text-[11px] shrink-0">
                  {files.map((file, idx) => (
                    <div key={idx} className="truncate font-mono py-0.5" title={file.name}>
                      {file.name} <span className="text-muted-foreground">({formatBytes(file.size)})</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bulk-upload-target" className="text-xs text-muted-foreground">Zielordner</Label>
                  <select
                    id="bulk-upload-target"
                    data-testid="bulk-upload-target"
                    value={targetFolder}
                    onChange={(event) => setTargetFolder(event.target.value)}
                    className="mt-1 min-h-11 min-[761px]:min-h-9 min-w-0 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm"
                  >
                    <option value="_Inbox">_Inbox</option>
                    <option value="Notes">Notes</option>
                    <option value="_Research">_Research</option>
                    <option value="_Research/Dossiers">_Research/Dossiers</option>
                    <option value="_Knowledge">_Knowledge</option>
                    <option value="__custom__">Eigener Ordner...</option>
                  </select>
                  {targetFolder === '__custom__' && (
                    <div className="mt-2 space-y-1">
                      <Label htmlFor="bulk-upload-custom-folder" className="text-xs text-muted-foreground">Eigener Zielordner</Label>
                      <Input
                        id="bulk-upload-custom-folder"
                        value={customFolder}
                        onChange={(event) => setCustomFolder(event.target.value)}
                        placeholder="_Inbox/Meetings"
                        className="font-mono"
                      />
                    </div>
                  )}
                  {resolvedTargetFolder.length > 0 &&
                    !KNOWLEDGE_COVERED_PREFIXES.some((prefix) => `${resolvedTargetFolder.replace(/\/+$/, '')}/`.startsWith(prefix)) && (
                      <p className="mt-2 text-xs text-amber-700" role="status">
                        Hinweis: Dieser Ordner ist nicht Teil des Wissensbereichs. Dateien dort werden
                        gespeichert, aber nicht automatisch im Wissen und in Antworten durchsuchbar.
                      </p>
                    )}
                </div>
                <div>
                  <Label htmlFor="bulk-upload-conflict" className="text-xs text-muted-foreground">Bei Namenskonflikt</Label>
                  <select
                    id="bulk-upload-conflict"
                    data-testid="bulk-upload-conflict"
                    value={conflictStrategy}
                    onChange={(event) => setConflictStrategy(event.target.value as VaultBulkConflictStrategy)}
                    className="mt-1 min-h-11 min-[761px]:min-h-9 min-w-0 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm"
                  >
                    <option value="rename">Umbenennen</option>
                    <option value="skip">Überspringen</option>
                    <option value="overwrite">Überschreiben</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Loading state */}
          {upload.isPending && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 shrink-0">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Importiere {files.length} Datei{files.length !== 1 ? 'en' : ''}…
              </p>
            </div>
          )}

          {/* Error state */}
          {uploadError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive shrink-0">
              {uploadError}
            </div>
          )}

          {/* Results */}
          {liveResults && (
            <div
              data-testid="bulk-upload-results"
              className="max-h-[50vh] overflow-auto rounded-md border border-border bg-muted/20 p-3 text-xs space-y-1"
            >
              <p className="text-[11px] text-muted-foreground mb-2">
                {liveResults.filter((item) => item.status === 'error').length === liveResults.length
                  ? 'Keine Datei konnte importiert werden.'
                  : 'Import abgeschlossen. Die Hintergrundverarbeitung (Analyse) läuft weiter.'}
              </p>
              {liveResults.map((item) => (
                <BulkUploadResultItem key={`${item.filename}-${item.path ?? item.status}`} item={item} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={upload.isPending}
          >
            Schliessen
          </Button>
          {!results && (
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={files.length === 0 || upload.isPending || !resolvedTargetFolder}
            >
              {upload.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Importieren
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function VaultPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedPath = searchParams.get('path')

  const [viewMode, setViewMode] = useState<ViewMode>('preview')
  // Memo: ob fuer den aktuellen Pfad der View-Mode vom User aktiv geaendert wurde.
  // Wenn ja, respektieren wir die Wahl und wenden den Default nicht erneut an.
  const userOverrodeViewMode = useRef<string | null>(null)
  const [localContent, setLocalContent] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)
  const [showNewFile, setShowNewFile] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [mobileShowTree, setMobileShowTree] = useState(true)

  const { data: filesData, isLoading: filesLoading } = useVaultFiles()
  const { data: fileData, isLoading: fileLoading } = useVaultFile(selectedPath)
  const { data: ingestionData } = useIngestionJobs()
  const putFile = usePutVaultFile()
  const deleteFile = useDeleteVaultFile()
  const retryIngestion = useRetryIngestionJob()
  const reparseFile = useReparseVaultFile()
  const removeDemo = useRemoveDemoContent()

  // Sync file content when file loads / selection changes
  useEffect(() => {
    if (fileData) {
      setLocalContent(fileData.content)
      setIsDirty(false)
    } else if (!selectedPath) {
      setLocalContent('')
      setIsDirty(false)
    }
  }, [fileData, selectedPath])

  // Default-View-Mode pro Pfad bestimmen — Dossiers/Sources auf Preview, Rest auf Edit.
  // User-Override per setViewMode bleibt fuer den selben Pfad erhalten.
  useEffect(() => {
    if (!selectedPath) return
    if (userOverrodeViewMode.current === selectedPath) return
    setViewMode(isReadOnlyByDefault(selectedPath) ? 'preview' : 'edit')
  }, [selectedPath])

  // Mobile-Ansicht: bei ausgewähltem Pfad (Direktlink oder Client-Navigation)
  // die Dokumentansicht zeigen; bei entferntem Pfad wieder die Dateiliste.
  // "← Liste" bei gleichbleibendem Pfad bleibt über setMobileShowTree(true) möglich.
  useEffect(() => {
    setMobileShowTree(!selectedPath)
  }, [selectedPath])

  // Source-File-Frontmatter fuer "Original-URL oeffnen"-Button
  const sourceUrl = useMemo(() => {
    if (!selectedPath || !isResearchSource(selectedPath)) return null
    const fm = fileData?.frontmatter as Record<string, unknown> | undefined
    if (fm && typeof fm['url'] === 'string' && fm['url']) return fm['url']
    // Fallback: aus localContent parsen, falls Backend-Frontmatter leer
    const parsed = parseFrontmatter(localContent)
    return fmString(parsed.data, 'url')
  }, [selectedPath, fileData?.frontmatter, localContent])

  const save = useCallback(
    async (content: string) => {
      if (!selectedPath) return
      try {
        await putFile.mutateAsync({ path: selectedPath, content })
        setIsDirty(false)
      } catch {
        toast.error('Speichern fehlgeschlagen')
      }
    },
    [selectedPath, putFile],
  )

  const autoSave = useDebounce(save, 2000)

  const handleEditorChange = useCallback(
    (value: string) => {
      setLocalContent(value)
      setIsDirty(true)
      autoSave(value)
    },
    [autoSave],
  )

  const handleManualSave = () => {
    if (selectedPath) void save(localContent)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') return
      if (!selectedPath || !isDirty || putFile.isPending) return
      event.preventDefault()
      void save(localContent)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isDirty, localContent, putFile.isPending, save, selectedPath])

  const handleSelectFile = (path: string) => {
    setSearchParams({ path })
    setMobileShowTree(false)
  }

  const handleNewFile = async (path: string) => {
    setShowNewFile(false)
    try {
      await putFile.mutateAsync({ path, content: '' })
      setSearchParams({ path })
      toast.success(`${path} erstellt`)
    } catch {
      toast.error('Datei konnte nicht erstellt werden')
    }
  }

  const handleNavigate = (wikilinkTarget: string) => {
    // Find file by name (without extension) or exact path
    const entries = filesData?.entries ?? []
    const match = entries.find(
      (e) =>
        e.type === 'file' &&
        (e.path === wikilinkTarget ||
          e.path === `${wikilinkTarget}.md` ||
          e.name === wikilinkTarget ||
          e.name === `${wikilinkTarget}.md`),
    )
    if (match) {
      setSearchParams({ path: match.path })
    } else {
      // Create stub file
      void handleNewFile(`${wikilinkTarget}.md`)
    }
  }

  const handleDelete = async () => {
    if (!selectedPath) return
    try {
      await deleteFile.mutateAsync(selectedPath)
      setShowDeleteDialog(false)
      setSearchParams({})
      toast.success('Datei gelöscht')
    } catch {
      toast.error('Löschen fehlgeschlagen')
      setShowDeleteDialog(false)
    }
  }

  // F05a2: Genau eine Instanz jedes Dialogs in VaultPage. Der tatsächlich
  // geklickte Auslöser (HTMLButtonElement) wird hier erfasst; onCloseAutoFocus
  // in den Dialogen gibt den Fokus an genau diesen Button zurück (Fallback nur,
  // wenn der Auslöser inzwischen unmounted ist). Mehrere sichtbare Launcher
  // teilen sich einen Root — nie zwei Dialog-Inhalte/Fokusfallen gleichzeitig.
  const dialogOpenerRef = useRef<HTMLButtonElement | null>(null)
  const sourcePanelRef = useRef<HTMLDivElement | null>(null)
  const restoreDialogFocus = (event: Event) => {
    event.preventDefault()
    const opener = dialogOpenerRef.current
    if (opener?.isConnected) opener.focus()
    else sourcePanelRef.current?.focus()
  }

  const openNewFileDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    dialogOpenerRef.current = event.currentTarget
    setShowNewFile(true)
  }
  const openBulkUploadDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    dialogOpenerRef.current = event.currentTarget
    setShowBulkUpload(true)
  }
  const openDeleteDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    dialogOpenerRef.current = event.currentTarget
    setShowDeleteDialog(true)
  }

  const entries = filesData?.entries ?? []
  // M8.1: Demo-Seed erkennen — steuert den Entfernen-Button in der Pipeline-Leiste.
  const hasDemoEntries = entries.some((e) => e.path.startsWith('_Beispiele/'))

  const handleRemoveDemo = async () => {
    const confirmed = window.confirm(
      'Alle als Beispiel markierten Inhalte werden vollständig entfernt. Eigene Dateien im Ordner _Beispiele bleiben erhalten. Fortfahren?',
    )
    if (!confirmed) return
    try {
      const result = await removeDemo.mutateAsync()
      if (selectedPath && result.removed.includes(selectedPath)) {
        setSearchParams({})
      }
      toast.success(`${result.removed.length} Beispiel-Dateien entfernt`)
      if (result.skipped.length > 0) {
        toast.success('Nicht markierte Dateien wurden behalten.')
      }
      const firstError = result.errors[0]
      if (firstError) {
        // Fail-loud: der erste Fehler wird sichtbar gemeldet statt verschluckt.
        toast.error(`Fehler beim Entfernen: ${firstError.message}`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Beispiel-Inhalte konnten nicht entfernt werden')
    }
  }

  const ingestionJobsByPath = useMemo(() => {
    const map = new Map<string, IngestionJob>()
    for (const job of ingestionData?.jobs ?? []) {
      if (!map.has(job.vault_file_path)) map.set(job.vault_file_path, job)
    }
    return map
  }, [ingestionData?.jobs])
  const selectedIngestionJob = selectedPath ? ingestionJobsByPath.get(selectedPath) : undefined

  // M2-B: Parser-Fehler-Dateien (Frontmatter type/parser_error) mit Original-Bytes
  // (storage_path) koennen aus den Original-Bytes neu verarbeitet werden.
  const selectedParserError = useMemo(() => {
    const fm = fileData?.frontmatter as Record<string, unknown> | undefined
    if (!fm) return null
    if (typeof fm['parser_error'] === 'string' && fm['parser_error']) return fm['parser_error']
    if (fm['type'] === 'parser-error') return 'Datei konnte nicht gelesen werden'
    return null
  }, [fileData?.frontmatter])
  const canReparse = Boolean(selectedParserError && fileData?.storage_path)
  // Retry nur fuer Zustaende, die das Backend auch annimmt (failed/cancelled);
  // completed_with_warnings wuerde sonst garantiert mit 409 scheitern.
  const canRetryIngestion =
    selectedIngestionJob?.status === 'failed' ||
    selectedIngestionJob?.status === 'cancelled'

  const handleRetryIngestion = async () => {
    if (!selectedIngestionJob) return
    try {
      await retryIngestion.mutateAsync(selectedIngestionJob.id)
      toast.success('Analyse wird erneut gestartet')
    } catch (err) {
      // 404 not_found / 409 not_retryable — Backend-Meldung direkt zeigen.
      toast.error(err instanceof Error ? err.message : 'Analyse konnte nicht wiederholt werden')
    }
  }

  const handleReparse = async () => {
    if (!selectedPath) return
    try {
      const result = await reparseFile.mutateAsync(selectedPath)
      if (result.parserError) {
        toast.warning(`Erneut verarbeitet — Datei weiterhin nicht lesbar: ${result.parserError}`)
      } else if (result.ingestion && 'error' in result.ingestion) {
        toast.warning(`Erneut verarbeitet — Analyse konnte nicht gestartet werden: ${result.ingestion.error}`)
      } else {
        toast.success('Datei erneut verarbeitet')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erneute Verarbeitung fehlgeschlagen')
    }
  }

  const sourceFiles = entries.filter((e) => e.type === 'file' && !e.path.startsWith('_Outputs/'))
  const storedCount = sourceFiles.length
  // Ehrliche Zaehler (F05a): "Verarbeitet" zaehlt Dateien im Wissensbereich
  // mit abgeschlossenen Ingestion-Jobs (completed/completed_with_warnings) —
  // das garantiert keine KI-Analyse, da runIngestion bei LLM-Fehler ein
  // Fallback-Result liefern und trotzdem completed melden kann.
  // "Im Wissensbereich" zaehlt Dateien unter den vom Retrieval abgedeckten
  // Pfaden — das ist keine fachliche Freigabe oder KI-Zulassung.
  const coveredFiles = sourceFiles.filter((e) =>
    KNOWLEDGE_COVERED_PREFIXES.some((prefix) => e.path.startsWith(prefix)),
  )
  const processedCount = coveredFiles.filter((e) => {
    const status = ingestionJobsByPath.get(e.path)?.status
    return status === 'completed' || status === 'completed_with_warnings'
  }).length
  const coveredCount = coveredFiles.length

  return (
    <div ref={sourcePanelRef} role="region" aria-label="Quellen" tabIndex={-1} className="flex h-full flex-col overflow-hidden">
      <SourcePipeline
        stored={storedCount}
        processed={processedCount}
        covered={coveredCount}
        currentSelection={selectedPath}
        action={
          hasDemoEntries ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="demo-remove"
              className="px-2 text-xs"
              onClick={() => void handleRemoveDemo()}
              disabled={removeDemo.isPending}
            >
              {removeDemo.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Beispiel-Inhalte entfernen
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Source sidebar */}
        <aside
          className={cn(
            'w-full shrink-0 border-r flex flex-col overflow-hidden bg-background lg:w-80',
            !mobileShowTree && 'hidden lg:flex',
          )}
        >
          <VaultTree
            entries={entries}
            selectedPath={selectedPath}
            onSelectFile={handleSelectFile}
            onNewFile={openNewFileDialog}
            onBulkUpload={openBulkUploadDialog}
            isLoading={filesLoading}
            ingestionJobsByPath={ingestionJobsByPath}
          />
        </aside>

        {/* Main area */}
        <div className={cn('flex flex-1 flex-col min-w-0 overflow-hidden', mobileShowTree && 'hidden lg:flex')}>
          {selectedPath ? (
            <>
              {/* Toolbar */}
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 min-h-11 bg-background py-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2 text-xs lg:hidden"
                  onClick={() => setMobileShowTree(true)}
                >
                  ← Liste
                </Button>

                <Breadcrumb path={selectedPath} />

                {isDirty && (
                  <span className="text-xs text-muted-foreground/60 shrink-0 hidden sm:inline">nicht gespeichert</span>
                )}

                {putFile.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
                )}

                {selectedIngestionJob && (
                  <IngestionStatusBadge job={selectedIngestionJob} />
                )}

                {canRetryIngestion && selectedIngestionJob && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2 text-xs shrink-0"
                    onClick={() => void handleRetryIngestion()}
                    disabled={retryIngestion.isPending}
                    title={selectedIngestionJob.last_error ?? selectedIngestionJob.error ?? 'Analyse erneut starten'}
                  >
                    {retryIngestion.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <RotateCw className="h-3.5 w-3.5 mr-1" />
                    )}
                    Analyse wiederholen
                  </Button>
                )}

                {canReparse && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2 text-xs shrink-0"
                    onClick={() => void handleReparse()}
                    disabled={reparseFile.isPending}
                    title={selectedParserError ?? 'Aus den Original-Bytes neu verarbeiten'}
                  >
                    {reparseFile.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <FileCog className="h-3.5 w-3.5 mr-1" />
                    )}
                    Erneut verarbeiten
                  </Button>
                )}

                <div className="flex items-center gap-1 shrink-0 ml-auto">
                  {sourceUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 text-xs hidden sm:inline-flex"
                      onClick={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
                      title="Original-URL in neuem Tab öffnen"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Original
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2 text-xs"
                    onClick={handleManualSave}
                    disabled={!isDirty || putFile.isPending}
                    title="Speichern (Strg+S)"
                  >
                    <Save className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden sm:inline">Speichern</span>
                  </Button>

                  <div className="flex items-center rounded-md border border-border p-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn('px-2 text-xs', viewMode === 'edit' && 'bg-accent text-accent-foreground')}
                      onClick={() => {
                        if (selectedPath) userOverrodeViewMode.current = selectedPath
                        setViewMode('edit')
                      }}
                      title="Markdown bearbeiten"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      <span className="hidden sm:inline">Bearbeiten</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn('px-2 text-xs', viewMode === 'preview' && 'bg-accent text-accent-foreground')}
                      onClick={() => {
                        if (selectedPath) userOverrodeViewMode.current = selectedPath
                        setViewMode('preview')
                      }}
                      title="Vorschau anzeigen"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      <span className="hidden sm:inline">Vorschau</span>
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground"
                    title="Versionshistorie (demnächst)"
                    aria-label="Versionshistorie (demnächst)"
                    disabled
                  >
                    <History className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={openDeleteDialog}
                    title="Datei löschen"
                    aria-label="Datei löschen"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Editor / Preview */}
              <div className="flex-1 overflow-hidden">
                {fileLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : viewMode === 'edit' ? (
                  <VaultEditor
                    key={selectedPath}
                    content={localContent}
                    onChange={handleEditorChange}
                    onNavigate={handleNavigate}
                  />
                ) : (
                  <VaultPreview
                    content={localContent}
                    onNavigate={handleNavigate}
                    filePath={selectedPath}
                    contentType={fileData?.content_type ?? 'text/markdown'}
                    storagePath={fileData?.storage_path ?? null}
                    imageMetadata={fileData?.image_metadata as Record<string, unknown> | undefined}
                  />
                )}
              </div>
            </>
          ) : (
            // Empty state
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="space-y-1 max-w-xs">
                <h2 className="text-sm font-semibold">Keine Quelle ausgewählt</h2>
                <p className="text-xs text-muted-foreground">
                  Wähle eine Quelle aus der Liste, importiere Dateien oder erstelle eine neue Notiz.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openBulkUploadDialog}>
                  Dateien importieren
                </Button>
                <Button size="sm" onClick={openNewFileDialog}>
                  Notiz erstellen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* F05a2: Genau ein Root pro Dialog — kontrolliert über die bestehenden
          open-States. Fokus-Rückgabe an den erfassten Auslöser. */}
      <NewFileDialog
        open={showNewFile}
        onOpenChange={setShowNewFile}
        onConfirm={handleNewFile}
        onCloseAutoFocus={restoreDialogFocus}
      />
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        onCloseAutoFocus={restoreDialogFocus}
      />
      {selectedPath && (
        <DeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          path={selectedPath}
          onConfirm={() => void handleDelete()}
          isLoading={deleteFile.isPending}
          onCloseAutoFocus={restoreDialogFocus}
        />
      )}
    </div>
  )
}
